import React, { useState, useRef, useEffect } from 'react';
import type { NodeType, NodeTemplate } from '../../types/canvas';
import { nodeTemplateService } from '../../services/nodeTemplateService';
import { ChevronDown, Link2, MessageSquare, Image, SlidersHorizontal, Zap, FileEdit, Undo2, Redo2, type LucideIcon } from 'lucide-react';

interface CanvasToolbarProps {
  onAddNode: (type: NodeType) => void;
  onAddTemplateNode: (template: NodeTemplate) => void;
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
}

const NODE_TYPES: Array<{ type: NodeType; label: string; icon: LucideIcon }> = [
  { type: 'prompt', label: 'Prompt', icon: MessageSquare },
  { type: 'upload', label: 'Media', icon: Image },
  { type: 'preset', label: 'Preset', icon: SlidersHorizontal },
  { type: 'generate', label: 'Generate', icon: Zap },
  { type: 'note', label: 'Note', icon: FileEdit },
];

export function CanvasToolbar({ onAddNode, onAddTemplateNode, onUndo, onRedo, canUndo = false, canRedo = false }: CanvasToolbarProps) {
  const [templates, setTemplates] = useState<NodeTemplate[]>([]);
  const [isTemplateDropdownOpen, setIsTemplateDropdownOpen] = useState(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsTemplateDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load templates when dropdown opens
  useEffect(() => {
    if (isTemplateDropdownOpen && templates.length === 0) {
      setIsLoadingTemplates(true);
      nodeTemplateService.list()
        .then(setTemplates)
        .catch(console.error)
        .finally(() => setIsLoadingTemplates(false));
    }
  }, [isTemplateDropdownOpen, templates.length]);

  const handleTemplateSelect = (template: NodeTemplate) => {
    onAddTemplateNode(template);
    setIsTemplateDropdownOpen(false);
  };

  return (
    <div className="absolute top-3 left-1/2 -translate-x-1/2 md:top-4 z-10 flex items-center gap-2">
      {/* Add node buttons - icon-only on mobile, with labels on desktop */}
      <div className="bg-white/90 backdrop-blur rounded-lg shadow-md border border-gray-200 px-1.5 md:px-2 py-1.5 flex items-center gap-0.5 md:gap-1">
        {NODE_TYPES.map((node) => (
          <button
            key={node.type}
            onClick={() => onAddNode(node.type)}
            title={`Add ${node.label} node`}
            className="flex items-center gap-1.5 px-2 md:px-3 py-1.5 md:h-7 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
          >
            <node.icon className="w-4 h-4 md:w-3.5 md:h-3.5" />
            <span className="hidden md:inline">{node.label}</span>
          </button>
        ))}

        {/* Separator */}
        <div className="w-px h-5 bg-gray-300 mx-1" />

        {/* Templates dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsTemplateDropdownOpen(!isTemplateDropdownOpen)}
            title="Add from template"
            className="flex items-center gap-1 px-2 md:px-3 py-1.5 md:h-7 text-xs text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-md transition-colors"
          >
            <Link2 className="w-4 h-4 md:w-3.5 md:h-3.5" />
            <span className="hidden md:inline">Templates</span>
            <ChevronDown className="w-3 h-3 hidden md:inline" />
          </button>

          {isTemplateDropdownOpen && (
            <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
              {isLoadingTemplates ? (
                <div className="px-3 py-2 text-xs text-gray-400">Loading...</div>
              ) : templates.length === 0 ? (
                <div className="px-3 py-2 text-xs text-gray-400">
                  No templates yet.
                  <br />
                  <span className="text-gray-500">Save a Generate node as template to get started.</span>
                </div>
              ) : (
                templates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => handleTemplateSelect(template)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors"
                  >
                    <div className="font-medium text-gray-700 truncate">{template.name}</div>
                    {template.description && (
                      <div className="text-xs text-gray-400 truncate">{template.description}</div>
                    )}
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Separator */}
        <div className="w-px h-5 bg-gray-300 mx-1" />

        {/* Undo/Redo buttons */}
        <button
          onClick={onUndo}
          disabled={!canUndo}
          title="Undo (Ctrl+Z)"
          className={`flex items-center gap-1.5 px-2 py-1.5 md:h-7 text-xs rounded-md transition-colors ${
            canUndo
              ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              : 'text-gray-300 cursor-not-allowed'
          }`}
        >
          <Undo2 className="w-4 h-4 md:w-3.5 md:h-3.5" />
        </button>
        <button
          onClick={onRedo}
          disabled={!canRedo}
          title="Redo (Ctrl+Shift+Z)"
          className={`flex items-center gap-1.5 px-2 py-1.5 md:h-7 text-xs rounded-md transition-colors ${
            canRedo
              ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              : 'text-gray-300 cursor-not-allowed'
          }`}
        >
          <Redo2 className="w-4 h-4 md:w-3.5 md:h-3.5" />
        </button>
      </div>
    </div>
  );
}
